import type { z } from "zod/v4";
import {
  resolveMetricAggregationForField,
  resolveRatioDependencyForDataset,
} from "@/lib/ai/visualization/data/adapters/metric-aggregation";
import type { VizAuthoringIssue } from "@/lib/ai/visualization/types";
import type { barComparisonAuthoringSchema } from "@/lib/ai/visualization/views/bar-comparison/schema";
import type { VizSpec } from "@/lib/ai/visualization/views/compiled-spec";
import { resolveBarLayout } from "@/lib/ai/visualization/views/shared/bar-layout";
import type {
  BuildVizElementArgs,
  BuiltVizElement,
} from "@/lib/ai/visualization/views/shared/element-builder";
import { buildSeriesLabelMap } from "@/lib/ai/visualization/views/shared/label-map";
import { buildSeriesEntries } from "@/lib/ai/visualization/views/shared/series-entries";
import {
  buildSpecValidationIssue,
  pushDuplicateValueIssues,
  pushMappedKeyIssues,
  pushProjectableMetricBindingIssue,
  pushRequiredMappedKeyCoverageIssues,
  pushRequiredTextIssue,
  pushSelectedKeyIssues,
  validateFieldRole,
} from "@/lib/ai/visualization/views/shared/spec-validation";
import type {
  ValidateVizSpecArgs,
  VizAuthoringCardDraft,
} from "@/lib/ai/visualization/views/shared/view-definition";

type BarComparisonSpec = Extract<VizSpec, { type: "bar-comparison" }>;
type BarComparisonAuthoring = z.infer<typeof barComparisonAuthoringSchema>;

/**
 * long-form比較がwide projectionへ変換できる実現済みseries数を返す。
 *
 * @remarks
 * rendererは2種類未満のseries labelを比較として扱えずthrowするため、空白と
 * 非finite値をrendererと同じ規則で除外し、compile前のbinding validationで止める。
 */
function countRealizedLongFormSeries(
  dataset: ValidateVizSpecArgs<BarComparisonSpec>["dataset"],
  categoryKey: string,
  seriesByKey: string,
  valueKey: string,
): number | null {
  const valueField = dataset.fields.find((field) => field.name === valueKey);
  if (!valueField) return null;
  const aggregation = resolveMetricAggregationForField(valueField);
  if (!aggregation || aggregation.kind === "non-aggregatable") return null;

  const normalizeDimension = (value: unknown): string | null => {
    if (typeof value === "string" && value.trim().length > 0) return value;
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(value);
    }
    return null;
  };

  if (aggregation.kind === "additive") {
    return new Set(
      dataset.rows.flatMap((row) => {
        const category = normalizeDimension(row[categoryKey]);
        const series = normalizeDimension(row[seriesByKey]);
        const value = row[valueKey];
        return category !== null &&
          series !== null &&
          typeof value === "number" &&
          Number.isFinite(value)
          ? [series]
          : [];
      }),
    ).size;
  }

  const dependency = resolveRatioDependencyForDataset({
    dataset,
    metricKey: valueKey,
  });
  if (!dependency) return null;
  const cells = new Map<
    string,
    {
      series: string;
      numeratorTotal: number;
      denominatorTotal: number;
      touched: boolean;
      hasNumerator: boolean;
      hasDenominator: boolean;
    }
  >();
  for (const row of dataset.rows) {
    const category = normalizeDimension(row[categoryKey]);
    const series = normalizeDimension(row[seriesByKey]);
    if (category === null || series === null) continue;

    const cellKey = JSON.stringify([category, series]);
    const cell = cells.get(cellKey) ?? {
      series,
      numeratorTotal: 0,
      denominatorTotal: 0,
      touched: false,
      hasNumerator: false,
      hasDenominator: false,
    };
    const numerator = row[dependency.numeratorKey];
    const denominator = row[dependency.denominatorKey];
    if (typeof numerator === "number" && Number.isFinite(numerator)) {
      cell.numeratorTotal += numerator;
      cell.touched = true;
      cell.hasNumerator = true;
    }
    if (typeof denominator === "number" && Number.isFinite(denominator)) {
      cell.denominatorTotal += denominator;
      cell.touched = true;
      cell.hasDenominator = true;
    }
    cells.set(cellKey, cell);
  }

  return new Set(
    [...cells.values()].flatMap((cell) => {
      const survivesProjection =
        cell.touched &&
        cell.hasNumerator &&
        cell.hasDenominator &&
        (cell.denominatorTotal !== 0 ||
          dependency.zeroDenominatorValue !== null);
      return survivesProjection ? [cell.series] : [];
    }),
  ).size;
}

/**
 * bar comparison の renderer-ready 制約を検証する。
 *
 * @remarks
 * dual axis は runtime component 側で wide metrics 前提の surface を持つため、
 * long-form binding と混在させず compile 時点で止める。
 */
export function validateBarComparisonSpec(
  args: ValidateVizSpecArgs<BarComparisonSpec>,
): VizAuthoringIssue[] {
  const issues: VizAuthoringIssue[] = [];
  validateFieldRole({
    issues,
    dataset: args.dataset,
    path: "spec.categoryKey",
    fieldName: args.spec.categoryKey,
    role: "dimension",
  });

  if (args.spec.seriesKeys.length === 0) {
    issues.push(
      buildSpecValidationIssue({
        path: "spec.seriesKeys",
        code: "missing-series-keys",
        message:
          "must include at least one metric key in spec.seriesKeys for a renderer-ready spec",
      }),
    );
  }
  pushDuplicateValueIssues({
    issues,
    path: "spec.seriesKeys",
    values: args.spec.seriesKeys,
  });
  args.spec.seriesKeys.forEach((seriesKey, index) => {
    validateFieldRole({
      issues,
      dataset: args.dataset,
      path: `spec.seriesKeys[${index}]`,
      fieldName: seriesKey,
      role: "metric",
    });
  });

  const hasSeriesBy = typeof args.spec.seriesByKey === "string";
  const hasValue = typeof args.spec.valueKey === "string";
  if (hasSeriesBy !== hasValue) {
    issues.push(
      buildSpecValidationIssue({
        path: "spec.(seriesByKey|valueKey)",
        code: "paired-fields-required",
        message: "spec.seriesByKey and spec.valueKey must be set together",
      }),
    );
  }
  if (hasSeriesBy && hasValue) {
    validateFieldRole({
      issues,
      dataset: args.dataset,
      path: "spec.seriesByKey",
      fieldName: args.spec.seriesByKey,
      role: "dimension",
    });
    validateFieldRole({
      issues,
      dataset: args.dataset,
      path: "spec.valueKey",
      fieldName: args.spec.valueKey,
      role: "metric",
    });
    if (
      args.spec.seriesKeys.length !== 1 ||
      args.spec.seriesKeys[0] !== args.spec.valueKey
    ) {
      issues.push(
        buildSpecValidationIssue({
          path: "spec.seriesKeys",
          code: "invalid-long-form-series-keys",
          message:
            "when spec.seriesByKey/spec.valueKey are used, spec.seriesKeys must be exactly [spec.valueKey]",
        }),
      );
    }
    const realizedSeriesCount = countRealizedLongFormSeries(
      args.dataset,
      args.spec.categoryKey,
      args.spec.seriesByKey!,
      args.spec.valueKey!,
    );
    if (realizedSeriesCount !== null && realizedSeriesCount < 2) {
      issues.push(
        buildSpecValidationIssue({
          path: "spec.seriesByKey",
          code: "insufficient-long-form-series",
          message:
            "long-form bar comparison requires at least two distinct realized series values",
          expected: "at least two distinct non-empty series values",
        }),
      );
    }
  }

  if (hasSeriesBy && hasValue) {
    pushProjectableMetricBindingIssue({
      issues,
      dataset: args.dataset,
      path: "spec.categoryKey",
      categoryKeys: [args.spec.categoryKey, args.spec.seriesByKey!],
      metricKeys: [args.spec.valueKey!],
      alwaysAggregate: true,
    });
  } else if (!hasSeriesBy && !hasValue) {
    pushProjectableMetricBindingIssue({
      issues,
      dataset: args.dataset,
      path: "spec.categoryKey",
      categoryKeys: [args.spec.categoryKey],
      metricKeys: args.spec.seriesKeys,
    });
  }

  const allowedSeriesKeys = new Set(args.spec.seriesKeys);
  pushRequiredTextIssue({
    issues,
    path: "spec.componentProps.categoryLabel",
    value: args.spec.componentProps?.categoryLabel ?? null,
    message:
      "categoryLabel is required because comparison chart labels are owned by the authoring output",
  });
  pushMappedKeyIssues({
    issues,
    path: "spec.componentProps.seriesLabels",
    entries: args.spec.componentProps?.seriesLabels,
    allowedKeys: allowedSeriesKeys,
    emptyAllowedMessage:
      "seriesLabels requires at least one metric key in spec.seriesKeys",
  });
  pushRequiredMappedKeyCoverageIssues({
    issues,
    path: "spec.componentProps.seriesLabels",
    entries: args.spec.componentProps?.seriesLabels,
    requiredKeys: args.spec.seriesKeys,
    emptyMessage:
      "seriesLabels must include every selected metric key because legend labels are owned by the authoring output",
  });
  pushSelectedKeyIssues({
    issues,
    path: "spec.componentProps.defaultVisibleSeriesKeys",
    selectedKeys: args.spec.componentProps?.defaultVisibleSeriesKeys ?? [],
    allowedKeys: allowedSeriesKeys,
    emptyAllowedMessage:
      "defaultVisibleSeriesKeys requires at least one metric key in spec.seriesKeys",
  });

  const yAxisMode = args.spec.componentProps?.yAxisMode ?? null;
  const seriesAxisMap = args.spec.componentProps?.seriesAxisMap ?? {};
  if (yAxisMode === "dual") {
    if (hasSeriesBy && hasValue) {
      issues.push(
        buildSpecValidationIssue({
          path: "spec.componentProps.yAxisMode",
          code: "dual-axis-wide-only",
          message:
            "dual yAxisMode requires the wide metric form and cannot be combined with spec.seriesByKey/spec.valueKey",
        }),
      );
    }
    if (args.spec.seriesKeys.length < 2) {
      issues.push(
        buildSpecValidationIssue({
          path: "spec.seriesKeys",
          code: "insufficient-series-for-dual-axis",
          message: "dual yAxisMode requires at least 2 metric keys",
        }),
      );
    }
    if (args.spec.stacking !== "grouped") {
      issues.push(
        buildSpecValidationIssue({
          path: "spec.stacking",
          code: "invalid-dual-axis-stacking",
          message: 'dual yAxisMode requires spec.stacking to be "grouped"',
          expected: "grouped",
          actual: args.spec.stacking,
        }),
      );
    }

    const assignments = Object.keys(seriesAxisMap);
    if (assignments.length === 0) {
      issues.push(
        buildSpecValidationIssue({
          path: "spec.componentProps.seriesAxisMap",
          code: "missing-series-axis-map",
          message:
            "dual yAxisMode requires spec.componentProps.seriesAxisMap with explicit left/right assignments",
        }),
      );
    }

    let hasLeft = false;
    let hasRight = false;
    for (const [key, axis] of Object.entries(seriesAxisMap)) {
      if (!allowedSeriesKeys.has(key)) {
        issues.push(
          buildSpecValidationIssue({
            path: `spec.componentProps.seriesAxisMap.${key}`,
            code: "invalid-series-axis-key",
            message: `seriesAxisMap key '${key}' must reference spec.seriesKeys`,
            expected: Array.from(allowedSeriesKeys).join(", "),
            actual: key,
          }),
        );
      }
      hasLeft = hasLeft || axis === "left";
      hasRight = hasRight || axis === "right";
    }
    for (const seriesKey of args.spec.seriesKeys) {
      if (!(seriesKey in seriesAxisMap)) {
        issues.push(
          buildSpecValidationIssue({
            path: `spec.componentProps.seriesAxisMap.${seriesKey}`,
            code: "missing-series-axis-assignment",
            message: `seriesAxisMap must include '${seriesKey}' from spec.seriesKeys`,
          }),
        );
      }
    }
    if (!hasLeft || !hasRight) {
      issues.push(
        buildSpecValidationIssue({
          path: "spec.componentProps.seriesAxisMap",
          code: "dual-axis-side-balance",
          message:
            "dual yAxisMode requires both left and right axis assignments",
        }),
      );
    }
  } else if (Object.keys(seriesAxisMap).length > 0) {
    issues.push(
      buildSpecValidationIssue({
        path: "spec.componentProps.seriesAxisMap",
        code: "unexpected-series-axis-map",
        message:
          "seriesAxisMap can be set only when spec.componentProps.yAxisMode is dual",
      }),
    );
  }

  return issues;
}

/**
 * structured authoring payload を `bar-comparison` 用の renderer-ready spec へ変換する。
 */
export function compileBarComparisonSpec(args: {
  datasetId: string;
  dataset: BuildVizElementArgs<BarComparisonSpec>["dataset"];
  card: VizAuthoringCardDraft | null;
  view: BarComparisonAuthoring;
}): BarComparisonSpec {
  const seriesKeys =
    args.view.bindingMode === "wide"
      ? args.view.seriesKeys
      : [args.view.valueKey];
  const seriesLabels =
    args.view.bindingMode === "wide"
      ? buildSeriesLabelMap({
          seriesKeys: args.view.seriesKeys,
          entries: args.view.seriesLabelEntries,
        })
      : {
          [args.view.valueKey]: args.view.valueLabel,
        };

  return {
    type: "bar-comparison",
    datasetId: args.datasetId,
    cardTitle: args.card?.title ?? null,
    categoryKey: args.view.categoryKey,
    seriesKeys,
    seriesByKey:
      args.view.bindingMode === "long" ? args.view.seriesByKey : null,
    valueKey: args.view.bindingMode === "long" ? args.view.valueKey : null,
    stacking: args.view.stacking,
    sort: args.view.sort,
    componentProps: {
      categoryLabel: args.view.categoryLabel,
      seriesLabels,
    },
  };
}

/**
 * bar-comparison 用の UI element を構築する。
 */
export function buildBarComparisonElement(
  args: BuildVizElementArgs<BarComparisonSpec>,
): BuiltVizElement {
  const seriesLabels = args.spec.componentProps?.seriesLabels ?? {};

  return {
    elementKey: args.chartKey,
    element: {
      type: "BarComparison",
      props: {
        dataPath: args.dataPath,
        categoryKey: args.spec.categoryKey,
        series: buildSeriesEntries(
          args.dataset,
          args.spec.seriesKeys,
          seriesLabels,
        ),
        layout: resolveBarLayout({
          dataset: args.dataset,
          categoryKey: args.spec.categoryKey,
        }),
        stacking: args.spec.stacking,
        sort: args.spec.sort,
        ...(args.spec.componentProps ?? {}),
      },
      children: [],
    },
  };
}
